import stgConfig from "../../../config";
const ENV_DEV = "dev";
const ENV_STAGING = "staging";
const ENV_PRODUCTION = "production";
const ENV_AUTHOR = "author";

/** Chat URL identifier entry; attributes are sent to GQL API as-is. */
export interface ChatUrlIdentifierEntry {
  attributes: Record<string, string>;
}

export default class Environment {
  static getWindowLocation() {
    return window.location;
  }

  static getSubdomain() {
    const urlParts = this.getWindowLocation().hostname.split(".");

    if (urlParts[1] === "dev") {
      return "localhost";
    }

    return urlParts[0];
  }

  static getSubdirectory() {
    const urlParts = this.getWindowLocation().pathname.split("/");
    if (urlParts[1] === "wac") {
      return urlParts.length > 1 ? urlParts[2] : "";
    } else {
      return urlParts[1];
    }
  }

  static isStagingPublicEnvironment() {
    return (
      this.getWindowLocation().hostname.indexOf("wac.stg.internal.atlassian") >
        -1 || this.getWindowLocation().hostname.indexOf("qa-wac.atlassian") > -1
    );
  }

  static getEnvironment() {
    const forceStaging = window.location.search.includes(
      "forceProdTesting=true"
    );

    if (forceStaging) {
      return ENV_PRODUCTION;
    }
    switch (this.getSubdomain()) {
      case "wac":
      case "qa-wac":
      case "author":
      case "proof":
        return ENV_STAGING;
      case "localhost":
      case "local":
        return ENV_DEV;
      case "truth":
      case "www":
      case "atlassian":
      case "bitbucket":
      case "sourcetreeapp":
      case "agile":
      case "git":
      case "software":
        return ENV_PRODUCTION;
      default:
        /* for omlet instances */
        return ENV_STAGING;
    }
  }

  static getInternationalSubdomain() {
    const hostnameSubdomain = this.getSubdomain();
    const validLocales = [
      "cn",
      "cs",
      "de",
      "es",
      "fr",
      "it",
      "ko",
      "hu",
      "nl",
      "ja",
      "pl",
      "br",
      "ro",
      "fi",
      "ru",
    ];

    if (validLocales.indexOf(hostnameSubdomain) === -1) {
      return false;
    }

    return hostnameSubdomain;
  }

  static getInternationalSubdirectory() {
    const subdirectory = this.getSubdirectory();
    const validLocales = [
      "zh",
      "cs",
      "de",
      "es",
      "fr",
      "it",
      "ko",
      "hu",
      "nl",
      "ja",
      "pl",
      "br",
      "ro",
      "fi",
      "ru",
    ];

    if (validLocales.indexOf(subdirectory) === -1) {
      return false;
    }

    return subdirectory;
  }

  static isInternationalSubdomain() {
    return !!this.getInternationalSubdomain();
  }

  static production() {
    return ENV_PRODUCTION;
  }

  static staging() {
    return ENV_STAGING;
  }

  static dev() {
    return ENV_DEV;
  }

  static author() {
    return ENV_AUTHOR;
  }

  static isProduction() {
    return this.getEnvironment() === this.production();
  }

  static isStaging() {
    return this.getEnvironment() === this.staging();
  }

  static isDev() {
    return this.getEnvironment() === this.dev();
  }

  static isAuthor() {
    return this.getSubdomain() === this.author();
  }

  static getIdentityBaseUrl() {
    if (this.isStaging() || this.isDev()) {
      return "https://id.stg.internal.atlassian.com";
    } else {
      return "https://id.atlassian.com";
    }
  }

  static getLoginUrl(skipEncoding: boolean) {
    const continueUrl = this.getWindowLocation().href;
    if (!skipEncoding) {
      return `${this.getIdentityBaseUrl()}/login?continue=${encodeURIComponent(
        continueUrl
      )}`;
    } else {
      return `${this.getIdentityBaseUrl()}/login?continue=${continueUrl}`;
    }
  }

  static getLogoutUrl() {
    const continueUrl = this.getWindowLocation().href;
    return `${this.getIdentityBaseUrl()}/logout?continue=${encodeURIComponent(
      continueUrl
    )}`;
  }

  static getSwitchAccountURL(continueUrlParam?: string) {
    const continueUrl = continueUrlParam
      ? continueUrlParam
      : this.getWindowLocation().href;
    return `${this.getIdentityBaseUrl()}/login/select-account?continue=${encodeURIComponent(
      continueUrl
    )}`;
  }

  static getManageProfileUrl(userEmail: string) {
    const applicationParam = encodeURIComponent("wac");
    const continueUrlParam = encodeURIComponent(
      `${this.getIdentityBaseUrl()}/manage-profile`
    );
    const loginHintParam = encodeURIComponent(userEmail);

    return `${this.getIdentityBaseUrl()}/login?application=${applicationParam}&continue=${continueUrlParam}&login_hint=${loginHintParam}`;
  }

  static getRelativePathFromWacUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      let pathname = urlObj.pathname.replace(/\/+$/, "");

      if (pathname.startsWith("/wac/")) {
        pathname = pathname.slice("/wac".length);
      }
      return pathname.replace(/^\/+/, "");
    } catch {
      const match = /\/\/[^/]+(\/[^#?]*)/.exec(url);
      if (match?.[1]) {
        let pathname = match[1].replace(/\/+$/, "");
        if (pathname.startsWith("/wac/")) {
          pathname = pathname.slice("/wac".length);
        }
        return pathname.replace(/^\/+/, "");
      }
      return "";
    }
  };

  static PATH_TO_CONTACT_FLOW_ID_PRODUCTION: Record<string, string> = {
    "software/jira/product-discovery": "39457874-4e30-4774-89a6-e086842bdcb6",
    "software/jira": "1825e6f7-75fc-47e2-b634-98923fa4a3b1",
    "software/loom": "dc335c33-9da5-4291-842b-80956f53d2b7",
    "software/jira/premium": "f7f8f1ba-fe53-45b0-95af-2c08e6cbeca4",
    "software/jira/features/automation": "f7f8f1ba-fe53-45b0-95af-2c08e6cbeca4",
    "software/jira/product-discovery/features/roadmaps": "f7f8f1ba-fe53-45b0-95af-2c08e6cbeca4",
    "software/jira/automation-template-library": "f7f8f1ba-fe53-45b0-95af-2c08e6cbeca4",
    "software/jira/guides/automation/overview": "f7f8f1ba-fe53-45b0-95af-2c08e6cbeca4",
    "software/confluence/premium": "94d7c907-bb35-4c55-8554-505cd3bcf6c3",
    "software/premium": "5ecfff7f-2fb6-4199-8961-bdb8e726b496",
    "blog/platform/change-management-in-cloud": "5ecfff7f-2fb6-4199-8961-bdb8e726b496",
    "webinars": "5ecfff7f-2fb6-4199-8961-bdb8e726b496",
    "try/contact": "5ecfff7f-2fb6-4199-8961-bdb8e726b496",
    "software/jira/service-management/demo": "2b5e02f3-9070-4149-bf7b-9585bc6e2ac8",
    "licensing/purchase-licensing": "fc32ee97-2597-4658-80fd-56bbb8da4289",
    "software/loom/loom-confluence": "7cf56869-3db7-431e-8311-b524e6065934",
    "software/loom/loom-jira": "199f4750-6436-4b88-a2db-c2b87c1bdaed",
    "software/loom/resources": "eadd2afc-29bb-4e9c-a0d6-370fd70186eb",
    "software/loom/ai-meeting": "364f6abb-5628-41c1-bf90-b3ca2486378d",
    "software/loom/editing": "a4addd51-eb96-46fc-b5a9-3f99c3d923de",
    "software/compass": "b77cd5d3-5625-4d76-8734-3ac50b482656"
  };

  static PATH_TO_CONTACT_FLOW_ID_STAGING: Record<string, string> = {
    "software/confluence/premium": "c0191453-349b-4362-bc9f-cca714d5b004",
    "software/loom/loom-confluence": "9f1756b6-8607-43b4-97b6-32f888713bf0",
    "software/loom/loom-jira": "863e7f31-092a-45c6-affa-02db50c7ef72",
    "software/loom": "42cee94e-c0db-43d2-bd45-3cca9d7adb61",
    "software/jira/product-discovery": "4cdb6e29-9088-4404-bbfe-dfdba35bd3d6",
    "software/loom/ai-meeting": "f1956561-00b8-4ee0-a6c2-2100d07feb5f",
    "software/loom/editing": "bc21d930-ac0d-4eec-9158-d2dde2097029",
    "software/compass": "c4b93665-a856-4a79-aaf4-618865283bcc",
    "software/loom/resources": "4b5e7c93-5e16-4479-8030-fcad737249a3",
    "software/jira/premium": "5523d24d-62cc-4f86-9890-fca40e79a29a",
    "software/jira/features/automation": "5523d24d-62cc-4f86-9890-fca40e79a29a",
    "software/jira/product-discovery/features/roadmaps": "5523d24d-62cc-4f86-9890-fca40e79a29a",
    "software/jira/automation-template-library": "5523d24d-62cc-4f86-9890-fca40e79a29a",
    "software/jira/guides/automation/overview":"5523d24d-62cc-4f86-9890-fca40e79a29a",
    "software/jira/service-management/demo": "bf6d3eeb-2456-4601-bc9e-45fab8805b6e",
    "licensing/purchase-licensing": "6dca16eb-5a85-4635-9e71-9857d7da94d4",
    "licensing": "6dca16eb-5a85-4635-9e71-9857d7da94d4",
    "software/jira": "7e776619-ede4-4975-a194-38c6b182fa0c",
    "try/contact": "ef54c226-7ccc-41c8-aaf0-c375a55e2f81",
    "software/premium": "ef54c226-7ccc-41c8-aaf0-c375a55e2f81", 
    "webinars": "ef54c226-7ccc-41c8-aaf0-c375a55e2f81"
  };

  /**
   * URL path/pattern -> chat URL identifier (object for future use cases).
   * Exact relative paths (from getRelativePathFromWacUrl) are checked first; then entries are
   * iterated in order and the first pattern that pathname/currentUrl includes is used.
   */
  static PATH_TO_CHAT_URL_IDENTIFIER: Record<string, ChatUrlIdentifierEntry> = {
    // Exact path matches (checked via getRelativePathFromWacUrl)
    "software/premium": { attributes: { Tag_URL: "url:premium_home_page" } },
    "software/jira/service-management/premium": { attributes: { Tag_URL: "url:jsm_cloud_premium_home" } },
    "software/confluence/premium/roadmap": { attributes: { Tag_URL: "url:confluence_premium_roadmap" } },
    "software/confluence/premium": { attributes: { Tag_URL: "url:confluence_cloud_premium_home" } },
    "try/contact": { attributes: { Tag_URL: "url:contact_us_atlassian_cloud" } },
    "jira/product-discovery": { attributes: { Tag_URL: "url:jpd_home_page" } },
    // Include-style rules (first match wins; more specific before general)
    "software/premium/manage-change-in-cloud": { attributes: { Tag_URL: "url:manage_change_in_cloud" } },
    "webinars/software/learn-about-jira-software-and-confluence-cloud-premium": {
      attributes: { Tag_URL: "url:learn_about_jsw_confluence_premium" },
    },
    "webinars/software/agile-planning": { attributes: { Tag_URL: "url:agile_planning_atlassian_premium" } },
    "blog/platform/change-management": { attributes: { Tag_URL: "url:change_management_blog" } },
    "software/jira/service-management/asset-and-configuration-management-helps-service-requests": {
      attributes: { Tag_URL: "url:jsm_asset_configuration_management_helps_service_requests" },
    },
    "software/jira/service-management/features/asset-and-configuration-management": {
      attributes: { Tag_URL: "url:jsm_asset_configuration_management" },
    },
    "software/jira/service-management/6-reasons-to-upgrade-to-Jira-Service-Management-Premium": {
      attributes: { Tag_URL: "url:jsm_6_reasons_upgrade_premium" },
    },
    "software/confluence/features": { attributes: { Tag_URL: "url:confluence_features" } },
    "software/confluence/team-calendars": { attributes: { Tag_URL: "url:confluence_team_calendars" } },
    "software/confluence/automation": { attributes: { Tag_URL: "url:confluence_automation" } },
    "software/jira/premium/university": { attributes: { Tag_URL: "url:jsw_premium_university" } },
    "software/jira/features/automation": { attributes: { Tag_URL: "url:jsw_automation" } },
    "software/jira/features/roadmaps": { attributes: { Tag_URL: "url:jsw_roadmaps" } },
    "software/jira/automation": { attributes: { Tag_URL: "url:jsw_automation_template_library" } },
    "software/jira/guides/expand": { attributes: { Tag_URL: "url:jsw_automation_basics" } },
    "software/jira/premium": { attributes: { Tag_URL: "url:jsw_cloud_premium_home" } },
    "licensing/": { attributes: { Tag_URL: "url:purchasing_licensing_faq" } },
    "wac/licensing": { attributes: { Tag_URL: "url:purchasing_licensing_faq" } },
    "software/compass/features": { attributes: { Tag_URL: "url:compass_features" } },
    "software/compass": { attributes: { Tag_URL: "url:compass" } },
    "software/jira/product-discovery/demo": { attributes: { Tag_URL: "url:jpd_demo" } },
    "software/jira/demo": { attributes: { Tag_URL: "url:jira_demo" } },
    "software/jira/work-management/demo": { attributes: { Tag_URL: "url:jira_demo" } },
    "software/jira/service-management/demo": { attributes: { Tag_URL: "url:jsm_demo" } },
    "software/confluence/demo": { attributes: { Tag_URL: "url:confluence_demo" } },
    "software/access/demo": { attributes: { Tag_URL: "url:atlassian_guard_demo" } },
    "purchasing-licensing": { attributes: { Tag_URL: "url:dca_pricing_billing_licensing" } },
    "software/jira/product-discovery/pricing": { attributes: { Tag_URL: "url:jpd_pricing" } },
    "purchase/product/jira-core.ondemand": { attributes: { Tag_URL: "url:jwm_quote_order_form_cart" } },
    "purchase/product/jira-servicedesk.ondemand": { attributes: { Tag_URL: "url:jsm_quote_order_form_cart" } },
    "purchase/product/confluence.ondemand": { attributes: { Tag_URL: "url:confluence_quote_order_form_cart" } },
    "purchase/product/jira-software.ondemand": { attributes: { Tag_URL: "url:jsw_quote_order_form_cart" } },
    "software/confluence/pricing": { attributes: { Tag_URL: "url:confluencing_pricing" } },
  };

  static getMacUrl() {
    if (this.isStaging() || this.isDev()) {
      return "https://my.stg.internal.atlassian.com";
    } else {
      return "https://my.atlassian.com";
    }
  }

  static getStartUrl() {
    if (this.isStaging() || this.isDev()) {
      return "https://start.stg.atlassian.com";
    } else {
      return "https://start.atlassian.com";
    }
  }

  static getAPIPrivateURL() {
    if (this.isStaging() || this.isDev()) {
      return "https://wac.stg.internal.atlassian.com/gateway/api";
    }

    return "https://www.atlassian.com/gateway/api";
  }

  static getMyAdminAtlasBaseURL() {
    if (this.isStaging() || this.isDev()) {
      return "https://admin.stg.atlassian.com";
    }

    return "https://admin.atlassian.com";
  }

  static getBaseURL() {
    if (this.isStaging() || this.isDev()) {
      return "https://wac.stg.internal.atlassian.com";
    }

    return "https://www.atlassian.com";
  }

  static getWacIntegrationUrl() {
    if (this.isStaging() || this.isDev()) {
      const useQaWAC =
        window.location.hostname.indexOf("qa-wac") > -1 ||
        window.location.hostname.indexOf("author") > -1;
      return `${
        useQaWAC
          ? "https://qa-wac.internal.atlassian.com"
          : stgConfig.stgWacInternalUrl
      }`;
    } else {
      return "https://www.atlassian.com";
    }
  }

  static getProductTld = (): string => {
    return this.getEnvironment() === ENV_PRODUCTION
      ? ".atlassian.net"
      : ".jira-dev.com";
  };

  static getBBIntegrationUrl() {
    if (this.isStaging() || this.isDev()) {
      return "https://integration.bb-inf.net";
    } else {
      return "https://bitbucket.org";
    }
  }

  static getOmniChannelEnvironment() {
    const env = this.getEnvironment();
    return env === ENV_PRODUCTION ? "prod" : "stg";
  }

  static getOmniChannelFlowId() {
    const env = this.getEnvironment();
    // Return different flow IDs based on environment
    switch (env) {
      case ENV_PRODUCTION:
        return (
          Environment.PATH_TO_CONTACT_FLOW_ID_PRODUCTION[
            Environment.getRelativePathFromWacUrl(window.location.href)
          ] || "c4b93665-a856-4a79-aaf4-618865283bcc"
        );
      case ENV_STAGING:
        return (
          Environment.PATH_TO_CONTACT_FLOW_ID_STAGING[
            Environment.getRelativePathFromWacUrl(window.location.href)
          ] || "c4b93665-a856-4a79-aaf4-618865283bcc"
        );
      default:
        return "c4b93665-a856-4a79-aaf4-618865283bcc"; // Dev flow ID (same for now)
    }
  }

  static getOmniChannelGraphQLUrl() {
    const env = this.getEnvironment();
    return env === ENV_PRODUCTION
      ? "https://atlassian.com/gateway/api/graphql"
      : "https://wac.stg.internal.atlassian.com/gateway/api/graphql";
  }

  static getChatProductKey(): string {
    return 'jira';
  }

  static getChatSupportTeam(): string {
    return 'DCA';
  }

  static getChatOffHoursFlowId(): string | undefined {
    const env = this.getEnvironment();
    switch (env) {
      case ENV_PRODUCTION:
        return '783e4ba0-771b-4ba6-b72b-dee29fe30201';
      case ENV_STAGING:
        return 'a4966f51-d84d-438c-b35f-723602f0b2af';
      default:
        return 'a4966f51-d84d-438c-b35f-723602f0b2af';
    }
  }

  /**
   * Returns attributes for the current URL to send to getChatConnectionConfigForSalesChannel GQL.
   * Exact path match (via getRelativePathFromWacUrl) is tried first; then PATH_TO_CHAT_URL_IDENTIFIER
   * is iterated in order and the first pattern that pathname or currentUrl includes is used.
   */
  static getChatUrlIdentifier(): Record<string, string> {
    const currentUrl = window.location.href;
    const relativePath = Environment.getRelativePathFromWacUrl(currentUrl);

    const exact = Environment.PATH_TO_CHAT_URL_IDENTIFIER[relativePath];
    if (exact) {
      return exact.attributes;
    }
    return { Tag_URL: "url:no_tag_applicable" };
  }
}
